/* Phase AL0 — Alvys integration settings card. Mounted inside the
 * "Integrations" accordion section of AdminSystemSettingsPage.
 *
 * Surface:
 *   - Toggle: integration enabled
 *   - base_url, api_version (defaults pre-filled)
 *   - client_id (plaintext)
 *   - client_secret (password-style; placeholder shows last-4 mask;
 *     blank input = keep current value)
 *   - Save + Test connection buttons
 *   - Status panel with last test timestamp + outcome
 */
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Truck, CheckCircle2, XCircle, KeyRound, AlertTriangle, Send, Inbox } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";


function _fmtTs(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch (_) {
        return iso;
    }
}


export default function AlvysSettingsCard() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState({
        enabled: false,
        base_url: "https://integrations.alvys.com",
        api_version: "v1",
        client_id: "",
        client_secret: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/system/alvys");
            setSettings(data);
            setForm({
                enabled: !!data.enabled,
                base_url: data.base_url || "https://integrations.alvys.com",
                api_version: data.api_version || "v1",
                client_id: data.client_id || "",
                client_secret: "",
            });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onSave = async () => {
        setSaving(true);
        try {
            // Empty client_secret → omit it (server keeps existing).
            // We never send a literal null here from this UI; the
            // "Clear secret" button below does that explicitly.
            const payload = {
                enabled: form.enabled,
                base_url: form.base_url,
                api_version: form.api_version,
                client_id: form.client_id,
            };
            if (form.client_secret.trim()) {
                payload.client_secret = form.client_secret.trim();
            }
            await api.patch("/admin/system/alvys", payload);
            toast.success("Alvys settings saved");
            setForm((f) => ({ ...f, client_secret: "" }));
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const onClearSecret = async () => {
        if (!window.confirm("Clear the saved Alvys Client Secret? Future API calls will fail until a new secret is saved.")) return;
        setSaving(true);
        try {
            await api.patch("/admin/system/alvys", { client_secret: null });
            toast.success("Alvys Client Secret cleared");
            setForm((f) => ({ ...f, client_secret: "" }));
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const onTest = async () => {
        setTesting(true);
        try {
            // If the user typed a new secret without saving yet, include
            // it as an override so the probe reflects what they're about
            // to commit.
            const overrides = {};
            if (form.client_secret.trim()) overrides.client_secret = form.client_secret.trim();
            if (form.client_id && form.client_id !== settings?.client_id) overrides.client_id = form.client_id;
            if (form.base_url && form.base_url !== settings?.base_url) overrides.base_url = form.base_url;
            if (form.api_version && form.api_version !== settings?.api_version) overrides.api_version = form.api_version;
            const { data } = await api.post("/admin/system/alvys/test-connection", overrides);
            toast.success(data.scope_probe || data.message || "Connected.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
            load();
        } finally {
            setTesting(false);
        }
    };

    const canTest = !!form.client_id.trim() && (!!form.client_secret.trim() || settings?.client_secret_configured);
    const lastOk = settings?.last_connection_test_ok;
    const lastAt = _fmtTs(settings?.last_connection_test_at);

    return (
        <Card className="lg:col-span-2" data-testid="alvys-settings-card">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <Truck size={16} className="text-blue-900" />
                    <CardTitle className="font-display text-xl">Alvys integration</CardTitle>
                </div>
                <CardDescription>
                    Connect your Alvys tenant for trailer lookup + prefill. Phase AL0 wires the OAuth2 handshake and Test Connection only — actual trailer lookup ships in AL1.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
                <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                    <div>
                        <Label className="text-sm font-medium" htmlFor="alvys-enabled">Alvys integration enabled</Label>
                        <p className="text-xs text-slate-500">Master switch. Future Alvys-backed features check this flag before calling out.</p>
                    </div>
                    <Switch
                        id="alvys-enabled"
                        data-testid="alvys-enabled-switch"
                        checked={!!form.enabled}
                        disabled={loading}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: !!v }))}
                    />
                </div>

                {/* Phase AL3 — Push status updates toggle (READ-ONLY for now). */}
                <div
                    className="flex items-center justify-between rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2"
                    data-testid="alvys-writes-readonly-row"
                >
                    <div>
                        <Label className="text-sm font-medium flex items-center gap-1.5" htmlFor="alvys-writes-enabled">
                            <Send size={12} className="text-slate-500" />
                            Push status updates to Alvys
                        </Label>
                        <p className="text-xs text-slate-500">
                            Coming soon — we record everything we'd push in the outbox today. Alvys' trailer-write scopes aren't open to us yet, so this toggle stays off.
                        </p>
                    </div>
                    <Switch
                        id="alvys-writes-enabled"
                        data-testid="alvys-writes-enabled-switch"
                        checked={!!settings?.writes_enabled}
                        disabled
                    />
                </div>

                {/* Phase AL3 — Outbox pending counter. */}
                {settings?.client_secret_configured && (
                    <div
                        data-testid="alvys-outbox-summary"
                        className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                    >
                        <Inbox size={14} className="text-blue-900" />
                        <span>
                            <strong data-testid="alvys-outbox-pending-count">{settings?.pending_outbox_count ?? 0}</strong>
                            {" "}status update{(settings?.pending_outbox_count ?? 0) === 1 ? "" : "s"} pending in the dry-run outbox.
                        </span>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <Label htmlFor="alvys-base-url">Base URL</Label>
                        <Input
                            id="alvys-base-url"
                            data-testid="alvys-base-url-input"
                            value={form.base_url}
                            onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
                            placeholder="https://integrations.alvys.com"
                            disabled={loading}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="alvys-api-version">API version</Label>
                        <Input
                            id="alvys-api-version"
                            data-testid="alvys-api-version-input"
                            value={form.api_version}
                            onChange={(e) => setForm((f) => ({ ...f, api_version: e.target.value }))}
                            placeholder="v1"
                            disabled={loading}
                        />
                    </div>
                    <div className="space-y-1 lg:col-span-2">
                        <Label htmlFor="alvys-client-id">Client ID</Label>
                        <Input
                            id="alvys-client-id"
                            data-testid="alvys-client-id-input"
                            value={form.client_id}
                            onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))}
                            placeholder="Client ID from Alvys Admin Portal → API Access"
                            disabled={loading}
                            autoComplete="off"
                        />
                    </div>
                    <div className="space-y-1 lg:col-span-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="alvys-client-secret">Client Secret</Label>
                            {settings?.client_secret_configured && (
                                <button
                                    type="button"
                                    data-testid="alvys-clear-secret-btn"
                                    onClick={onClearSecret}
                                    className="text-xs text-red-600 hover:text-red-700 underline"
                                    disabled={saving}
                                >
                                    Clear saved secret
                                </button>
                            )}
                        </div>
                        <Input
                            id="alvys-client-secret"
                            data-testid="alvys-client-secret-input"
                            type="password"
                            autoComplete="new-password"
                            value={form.client_secret}
                            onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))}
                            placeholder={
                                settings?.client_secret_masked
                                    ? `Saved: ${settings.client_secret_masked} — leave blank to keep`
                                    : "Paste your Alvys Client Secret"
                            }
                            disabled={loading}
                        />
                        <p className="text-xs text-slate-500 flex items-center gap-1.5">
                            <KeyRound size={12} />
                            Encrypted at rest (AES-GCM). Leave blank to keep the existing value. Click "Clear saved secret" to delete.
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        onClick={onSave}
                        disabled={saving || loading}
                        data-testid="alvys-save-btn"
                        className="bg-blue-900 hover:bg-blue-800"
                    >
                        {saving ? "Saving…" : "Save"}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onTest}
                        disabled={testing || loading || !canTest}
                        data-testid="alvys-test-btn"
                    >
                        {testing ? "Testing…" : "Test connection"}
                    </Button>
                    {!canTest && !loading && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                            <AlertTriangle size={12} /> Enter Client ID + Client Secret to enable the connection test.
                        </span>
                    )}
                </div>

                {settings?.last_connection_test_at && (
                    <div
                        data-testid="alvys-status-panel"
                        className={`rounded-md border px-3 py-2 text-sm flex items-start gap-2 ${
                            lastOk
                                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                                : "border-red-200 bg-red-50 text-red-800"
                        }`}
                    >
                        {lastOk ? <CheckCircle2 size={16} className="mt-0.5" /> : <XCircle size={16} className="mt-0.5" />}
                        <div>
                            <div className="font-medium">
                                {lastOk ? "Connected" : "Last connection attempt failed"}
                            </div>
                            <div className="text-xs opacity-90">
                                Tested {lastAt}
                                {settings.last_connection_test_error
                                    ? ` — ${settings.last_connection_test_error}`
                                    : ""}
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
